//
//  app_device_info_manager.js
//  Cell-Ed
//
//  Copyright © 2020 Cell-Ed. All rights reserved.
//

import { Plugins, Capacitor } from '@capacitor/core';
const { Device, App } = Plugins;

import moment from 'moment-timezone';

const APP_DEVICE_INFO_CACHE = 'deviceInfoCache';

/*
 * Capacitor 3 moved three fields off Device.getInfo(): the app's own version and build went
 * to App.getInfo(), and the device id to Device.getId(). The Android app runs Capacitor 8
 * natively while this bundle still ships the Capacitor 2 JS, so on Android `info.uuid`,
 * `info.appVersion` and `info.appBuild` have all been undefined -- silently, because they
 * are only ever stringified into the `device-info` request header, where absent keys just
 * disappear.
 *
 * The old fields are read first, so a client still on a Capacitor 2 runtime keeps working
 * unchanged; the newer calls are only a fallback. Every one of them is failure-tolerant --
 * in a plain browser App.getInfo() rejects as unimplemented, which is not an error worth
 * failing device-info collection over.
 */
const pluginAvailable = (name) => {
  try {
    return Capacitor.isPluginAvailable(name);
  } catch (error) {
    return false;
  }
};

const resolveAppVersion = (info) => {
  if (info.appVersion || info.appBuild) {
    return Promise.resolve({ appVersion: info.appVersion || '', appBuild: info.appBuild || '' });
  }
  // Capacitor 2 registers a web shim for App, so isPluginAvailable('App') is true
  // in a browser -- but that shim has no getInfo (only Device does), so calling it
  // throws for every non-native user.
  if (!pluginAvailable('App') || typeof App.getInfo !== 'function') {
    return Promise.resolve({ appVersion: '', appBuild: '' });
  }
  return App.getInfo()
    .then((appInfo) => ({ appVersion: appInfo.version || '', appBuild: appInfo.build || '' }))
    .catch(() => ({ appVersion: '', appBuild: '' }));
};

const resolveDeviceId = (info) => {
  if (info.uuid) {
    return Promise.resolve(info.uuid);
  }
  if (!pluginAvailable('Device')) {
    return Promise.resolve('no_id');
  }
  return Device.getId()
    // `identifier` since Capacitor 5; `uuid` on the versions between.
    .then((deviceId) => deviceId.identifier || deviceId.uuid || 'no_id')
    .catch(() => 'no_id');
};

export const getAppDeviceInfo = () => {
  return JSON.parse(window.localStorage.getItem(APP_DEVICE_INFO_CACHE));
};

export const loadAppDeviceInfo = (getDeviceInfo) => {
  Device.getInfo().then((info) => {
    let userEnvIsMobile;
    switch (info.operatingSystem) {
      case 'android':
        userEnvIsMobile = true;
        break;
      case 'ios':
        userEnvIsMobile = true;
        break;
      case 'windows':
        userEnvIsMobile = false;
        break;
      case 'mac':
        userEnvIsMobile = false;
        break;
      default:
        userEnvIsMobile = false;
        break;
    }
    return Promise.all([resolveAppVersion(info), resolveDeviceId(info)]).then(
      ([appInfo, deviceId]) => {
        const deviceInfo = {
          id: deviceId,
          model: info.model,
          platform: info.platform,
          manufacturer: info.manufacturer,
          os: info.operatingSystem,
          osVersion: info.osVersion,
          appVersion: appInfo.appVersion,
          appBuild: appInfo.appBuild,
          timezone: moment.tz.guess(),
          userEnvIsMobile,
        };

        saveDeviceInfo(APP_DEVICE_INFO_CACHE, deviceInfo);
        if (getDeviceInfo) {
          getDeviceInfo(deviceInfo);
        }
      }
    );
  });
};

function saveDeviceInfo(key, value) {
  if (window.localStorage.getItem(key) !== JSON.stringify(value)) {
    window.localStorage.setItem(key, JSON.stringify(value));
  }
}
